import * as React from 'react'
import { Dispatch, ReactElement, SetStateAction, useMemo } from 'react'
import { InteractionTag, Text } from '@fluentui/react-components'

import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { InvoiceApprovalStatus, UserInvoiceApprovalDto } from './InvoiceApprovalOverview'
import { useFormikContext } from 'formik'
import PersonAvatar from '../../components/PersonAvatar'
import useAxios from '../../hooks/useAxios'
import useUsers from '../../hooks/useUsers'
import { useOverviewStyles } from '../BaseOverview/BaseOverview'
import { getTemplateInterval } from '../../components/Form/Finance/Invoice/InvoiceFile'
import { InvoiceApprovalMutationDto, UserInvoiceApprovalMutationDto, ViewInvoiceApprovalComponentProps } from './ViewInvoiceApproval'
import BaseFixedPriceOverview, {
    BaseFixedPriceOverviewProps,
    ExtendedFixedPriceOverviewDto,
    FixedPriceFilterType,
} from '../FixedPrice/BaseFixedPriceOverview'

export type ViewInvoiceApprovalFixedPricesProps = ViewInvoiceApprovalComponentProps & {
    setFixedPricesCount: Dispatch<SetStateAction<number>>
    setFixedPrices: Dispatch<SetStateAction<ExtendedFixedPriceOverviewDto[]>>
    selectedFixedPrices: ExtendedFixedPriceOverviewDto[]
}

export function FixedPricesHeaderFilterTags(props: BaseFixedPriceOverviewProps & { userApprovals: UserInvoiceApprovalMutationDto[] }) {
    const { values, setValues } = useFormikContext<FixedPriceFilterType>()
    const overviewStyles = useOverviewStyles()
    const { getUserById } = useUsers()
    const { viewport, isViewport } = useViewport()

    const listItemSmall = useMemo<boolean>(() => isViewport('<', Viewport.XL), [viewport])

    return (
        <div className={overviewStyles.flexGapXS}>
            {props.userApprovals?.map((item, index) => (
                <InteractionTag
                    key={index}
                    selected={values.tag === item.userId}
                    onClick={() => setValues({ ...props.defaultFilterValues, users: [item.userId], tag: item.userId })}
                    size={listItemSmall ? 'small' : 'medium'}
                >
                    <PersonAvatar name={getUserById(item.userId)?.fullName} color={'colorful'} size={24} />
                </InteractionTag>
            ))}
        </div>
    )
}

export default function ViewInvoiceApprovalFixedPrices(props: ViewInvoiceApprovalFixedPricesProps): ReactElement {
    const axios = useAxios()
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<InvoiceApprovalMutationDto>()

    const defaultFilterValues = useMemo<FixedPriceFilterType>(
        () => ({
            from: getTemplateInterval(props.invoiceTemplate ? [props.invoiceTemplate] : []).start.toISODate(),
            to: getTemplateInterval(props.invoiceTemplate ? [props.invoiceTemplate] : []).end.toISODate(),
            users: [],
            isBilled: props.approvalStatus === InvoiceApprovalStatus.Done,
            files: values.fileIds ? values.fileIds : [],
            fileOwners: [],
            invoiceApprovals: values.id ? [values.id] : [],
            attributes: [],
            query: '',
        }),
        [values, props.invoiceTemplate],
    )

    const fixedFilterValues = useMemo<FixedPriceFilterType>(
        () => ({
            files: values.fileIds || [],
            invoiceApprovals: values.id ? [values.id] : [],
        }),
        [values, props.invoiceTemplate],
    )

    return (
        <div className={props.hiddenClassName ? props.hiddenClassName : undefined}>
            {values.fileIds?.length === 0 && <Text>{translate('invoiceapproval.userapprovals.selectfile')}</Text>}
            {values.fileIds?.length > 0 && (
                <BaseFixedPriceOverview
                    baseUrl={`/invoice-approval/${props.approval?.id}/fixed-price`}
                    hideFileField
                    hideInvoiceField
                    focusQueryOnRender={false}
                    renderLink={!!values.id}
                    headerDefaultFilterTags={filterProps => (
                        <FixedPricesHeaderFilterTags
                            {...(filterProps as BaseFixedPriceOverviewProps)}
                            baseUrl={`/invoice-approval/${props.approval?.id}/fixed-price`}
                            userApprovals={values.userApprovals}
                        />
                    )}
                    fixedFilterValues={fixedFilterValues}
                    defaultFilterValues={defaultFilterValues}
                    batchSizeItems={50}
                    onCountItems={(_column, result) => props.setFixedPricesCount(result.total)}
                    onLoadItems={(_column, _columnPages, items) => props.setFixedPrices(items || [])}
                    alwaysLoadAllItems
                    isItemSelectable={() => props.isApprovalActive}
                    isAllItemsSelectable={() => props.isApprovalActive}
                    isItemSelected={item => props.selectedFixedPrices.includes(item)}
                    isAllItemsSelected={items => items.filter(item => !props.selectedFixedPrices.includes(item)).length === 0}
                    onItemSelected={(data, item) => {
                        if (data.checked === true) {
                            setFieldValue('fixedPrices', [...values.fixedPrices, item.id])
                            if (values.id) {
                                axios
                                    .post<UserInvoiceApprovalDto[]>(`/finance/invoice-approval/${values.id}/fixed-price?id=${item.id}`)
                                    .then(response => {
                                        if (!!response?.data) {
                                            setFieldValue('userApprovals', [...values.userApprovals, ...response.data])
                                        }
                                    })
                            }
                        } else {
                            setFieldValue('fixedPrices', [...values.fixedPrices.filter(fixedPrice => fixedPrice !== item.id)])
                            if (values.id) {
                                axios.delete(`/finance/invoice-approval/${values.id}/fixed-price?id=${item.id}`)
                            }
                        }
                    }}
                    onAllItemsSelected={(data, items) => {
                        if (data.checked === true) {
                            setFieldValue(
                                'fixedPrices',
                                items.map(item => item.id),
                            )
                            if (values.id) {
                                axios
                                    .post<
                                        UserInvoiceApprovalDto[]
                                    >(`/finance/invoice-approval/${values.id}/fixed-price?id=${items.map(item => item.id).join(',')}`)
                                    .then(response => {
                                        if (!!response?.data) {
                                            setFieldValue('userApprovals', [...values.userApprovals, ...response.data])
                                        }
                                    })
                            }
                        } else {
                            setFieldValue('fixedPrices', [])
                            if (values.id) {
                                axios.delete(`/finance/invoice-approval/${values.id}/fixed-price?id=${items.map(item => item.id).join(',')}`)
                            }
                        }
                    }}
                    selectedItems={props.selectedFixedPrices}
                />
            )}
        </div>
    )
}
